/**
 * Credential Admin Overview Tab — sl-kqzl.3.3
 *
 * Workspace Admin/Owner governance view. Shows metadata-only table of all
 * credentials with creator, creation time, workflow usage count, and a
 * Transfer-Ownership action. No secrets/masked hints are displayed.
 */
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as permStyles from '../permissions/styles.css';
import { TYPE_LABELS } from './add-credential-modal';
import * as styles from './admin-overview-tab.css';
import type {
  CredentialAdminOverviewEntry,
  CredentialUsageInfo,
  WorkspaceCredentialInfo,
} from './use-workspace-credentials';

interface MemberLookup {
  id: string;
  name: string;
  email: string;
}

interface AdminOverviewTabProps {
  listAdminOverview: (
    signal?: AbortSignal
  ) => Promise<CredentialAdminOverviewEntry[]>;
  getCredentialUsage: (credentialId: string) => Promise<CredentialUsageInfo[]>;
  onTransferRequest: (credential: WorkspaceCredentialInfo) => void;
  /**
   * Used to reload when a transfer happens in parent. Parent increments
   * this value so we re-fetch. Start at 0.
   */
  refreshKey: number;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export function AdminOverviewTab({
  listAdminOverview,
  getCredentialUsage,
  onTransferRequest,
  refreshKey,
}: AdminOverviewTabProps) {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;

  const [entries, setEntries] = useState<CredentialAdminOverviewEntry[]>([]);
  const [members, setMembers] = useState<Map<string, MemberLookup>>(new Map());
  const [usageCounts, setUsageCounts] = useState<Map<string, number | null>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [fetched, memberResult] = await Promise.all([
          listAdminOverview(ac.signal),
          serverService.server?.gql({
            query: getMembersByWorkspaceIdQuery,
            variables: { workspaceId, take: 100 },
          }),
        ]);
        if (ac.signal.aborted) return;
        setEntries(fetched);
        const memberMap = new Map<string, MemberLookup>();
        for (const m of (memberResult?.workspace?.members ?? []) as Array<{
          id: string;
          name: string;
          email: string;
        }>) {
          memberMap.set(m.id, { id: m.id, name: m.name, email: m.email });
        }
        setMembers(memberMap);
      } catch (err) {
        if (ac.signal.aborted) return;
        setLoadError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    };
    void load();
    return () => ac.abort();
  }, [listAdminOverview, serverService.server, workspaceId, refreshKey]);

  // Load usage counts per credential after entries are loaded. One failing
  // credential must not blank the whole column, so each fetch has its own
  // error boundary (→ null = "unknown", renders as em-dash).
  useEffect(() => {
    if (entries.length === 0) return;
    let cancelled = false;
    const loadUsage = async () => {
      const results = await Promise.all(
        entries.map(async e => {
          try {
            const usages = await getCredentialUsage(e.id);
            return [e.id, usages.length] as const;
          } catch {
            return [e.id, null] as const;
          }
        })
      );
      if (cancelled) return;
      setUsageCounts(new Map(results));
    };
    void loadUsage();
    return () => {
      cancelled = true;
    };
  }, [entries, getCredentialUsage]);

  const handleTransfer = useCallback(
    (entry: CredentialAdminOverviewEntry) => {
      const creator = members.get(entry.createdBy);
      onTransferRequest({
        id: entry.id,
        workspaceId,
        name: entry.name,
        type: entry.type,
        service: entry.service,
        status: entry.status,
        maskedHint: '',
        lastUsedAt: entry.lastUsedAt,
        lastErrorAt: null,
        lastError: null,
        createdAt: entry.createdAt,
        updatedAt: entry.createdAt,
        createdBy: creator?.id ?? entry.createdBy,
        canRevealSecret: false,
      });
    },
    [members, onTransferRequest, workspaceId]
  );

  const rows = useMemo(
    () =>
      entries.map(entry => {
        const creator = members.get(entry.createdBy);
        const creatorLabel = creator?.name || creator?.email || entry.createdBy;
        const usage = usageCounts.get(entry.id);
        return { entry, creatorLabel, usage };
      }),
    [entries, members, usageCounts]
  );

  if (isLoading) {
    return (
      <div className={permStyles.loadingState}>
        {t['com.affine.loading']?.() ?? 'Loading...'}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={permStyles.emptyState} data-testid="admin-overview-error">
        {loadError}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={permStyles.emptyState} data-testid="admin-overview-empty">
        {t[
          'com.streamlain.settings.workspace.credentials.admin-overview.empty'
        ]?.() ?? 'No credentials in this workspace.'}
      </div>
    );
  }

  return (
    <div data-testid="admin-overview-table">
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.theadCell}>
              {t[
                'com.streamlain.settings.workspace.credentials.admin-overview.col.name'
              ]?.() ?? 'Name'}
            </th>
            <th className={styles.theadCell}>
              {t[
                'com.streamlain.settings.workspace.credentials.admin-overview.col.type'
              ]?.() ?? 'Type'}
            </th>
            <th className={styles.theadCell}>
              {t[
                'com.streamlain.settings.workspace.credentials.admin-overview.col.created-by'
              ]?.() ?? 'Created by'}
            </th>
            <th className={styles.theadCell}>
              {t[
                'com.streamlain.settings.workspace.credentials.admin-overview.col.created-at'
              ]?.() ?? 'Created at'}
            </th>
            <th className={styles.theadCell}>
              {t[
                'com.streamlain.settings.workspace.credentials.admin-overview.col.workflows'
              ]?.() ?? 'Workflows using'}
            </th>
            <th className={styles.theadCell} />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ entry, creatorLabel, usage }) => (
            <tr
              key={entry.id}
              className={styles.row}
              data-testid={`admin-overview-row-${entry.id}`}
            >
              <td className={styles.nameCell}>{entry.name}</td>
              <td className={styles.mutedCell}>
                {TYPE_LABELS[entry.type] ?? entry.type} · {entry.service}
              </td>
              <td className={styles.mutedCell}>{creatorLabel}</td>
              <td className={styles.mutedCell}>
                {formatDate(entry.createdAt)}
              </td>
              <td className={styles.mutedCell}>
                {usage == null ? '…' : usage}
              </td>
              <td className={styles.actionsCell}>
                <button
                  className={styles.transferButton}
                  data-testid={`admin-overview-transfer-${entry.id}`}
                  onClick={() => handleTransfer(entry)}
                >
                  {t[
                    'com.streamlain.settings.workspace.credentials.admin-overview.transfer-cta'
                  ]?.() ?? 'Transfer'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
